/**
 * ScopePreviewSheet — pre-acceptance preview for a contractor opportunity.
 *
 * Opened from ContractorOpportunitiesPanel BEFORE "Request Offer". Shows:
 *   • Scope photos (curated > consultation set)
 *   • Job Brief (arrival, days, crew, risk flags)
 *   • Plain-language scope summary
 *   • Payout amount only (no contractValue, no internal pricing)
 *   • Single CTA: Request offer
 *
 * Read-only. No mutations. Acceptance routes through the existing
 * useContractorInterest.submit() — passed in via props.
 */

import { Camera, MapPin, DollarSign } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAppData } from "@/data/AppDataContext";
import { getJobBriefing } from "@/data/contractor/jobBriefing";
import { getPortalSpec } from "@/data/portal/contractorPortalSelectors";
import { JobBriefBlock } from "@/components/contractor/JobBriefBlock";

export interface ScopePreviewSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string | null;
  /** Display-only payout from the visible-opportunity row. */
  payoutAmount?: number;
  /** Whether this contractor already has an open interest for the job. */
  alreadyRequested?: boolean;
  onRequest: () => void;
}

export function ScopePreviewSheet({
  open,
  onOpenChange,
  jobId,
  payoutAmount,
  alreadyRequested,
  onRequest,
}: ScopePreviewSheetProps) {
  const { jobs } = useAppData();
  const job = jobId ? jobs.find((j) => j.id === jobId) : undefined;
  const spec = job ? getPortalSpec(job) : undefined;
  const brief = job ? getJobBriefing(job, spec) : undefined;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        className="max-h-[92vh] overflow-y-auto"
        data-testid="scope-preview-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-base">Job preview</SheetTitle>
          <SheetDescription className="text-xs">
            Review the scope before requesting this job.
          </SheetDescription>
        </SheetHeader>

        {!job || !brief ? (
          <p className="py-6 text-xs text-muted-foreground">
            Job no longer available.
          </p>
        ) : (
          <div className="mt-3 space-y-4">
            {/* Header line */}
            <div>
              <p className="text-sm font-semibold">{job.projectType}</p>
              <p className="text-[11px] text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin className="h-3 w-3" />
                {job.address.city}
                {job.address.zip ? ` · ${job.address.zip}` : ""}
              </p>
              {payoutAmount != null && (
                <div className="flex items-center gap-1 text-sm font-bold mt-1.5">
                  <DollarSign className="h-3.5 w-3.5" />
                  {payoutAmount.toLocaleString()}
                  <span className="text-[10px] font-normal text-muted-foreground ml-1">
                    payout
                  </span>
                </div>
              )}
            </div>

            {/* Photos */}
            <section data-testid="scope-photos">
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1.5 flex items-center gap-1">
                <Camera className="h-3 w-3" /> Site photos
              </p>
              {brief.scopePhotos.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/20 px-3 py-4 text-[11px] text-muted-foreground text-center">
                  No site photos uploaded yet. Ask your PM before accepting.
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-1.5">
                  {brief.scopePhotos.map((src, i) => (
                    <div
                      key={i}
                      className="aspect-square rounded-md overflow-hidden bg-muted border border-border"
                    >
                      <img
                        src={src}
                        alt={`Site photo ${i + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Brief */}
            <JobBriefBlock brief={brief} />

            {/* Scope summary */}
            <section>
              <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">
                Scope
              </p>
              <p className="text-xs text-foreground/90 leading-relaxed">
                {brief.scopeSummary}
              </p>
              {spec && (
                <Badge variant="outline" className="text-[10px] mt-2">
                  Spec: {spec.label}
                </Badge>
              )}
            </section>

            {/* CTA */}
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t border-border">
              <Button
                className="w-full"
                disabled={alreadyRequested}
                onClick={() => {
                  onRequest();
                  onOpenChange(false);
                }}
              >
                {alreadyRequested ? "Already requested" : "Request this job"}
              </Button>
              <p className="text-[10px] text-muted-foreground text-center mt-1.5">
                A PM reviews every request. No commitment until they offer.
              </p>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
